import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Button, IconButton, TextField, Typography, InputAdornment, Chip } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const msgs = action.payload;
    const newMsgs = [];
    if (isArray(msgs)) {
      msgs.forEach(m => {
        const idx = state.findIndex(u => u.id === m.id);
        if (idx !== -1) state[idx] = m; else newMsgs.push(m);
      });
    }
    return [...state, ...newMsgs];
  }
  if (action.type === "UPDATE_QUICKMESSAGES") {
    const m = action.payload;
    const idx = state.findIndex(u => u.id === m.id);
    if (idx !== -1) { state[idx] = m; return [...state]; }
    return [m, ...state];
  }
  if (action.type === "DELETE_QUICKMESSAGE") {
    const id = action.payload;
    const idx = state.findIndex(u => u.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1, width: "100%", maxWidth: "100%",
    padding: theme.spacing(2), height: "100%",
    display: "flex", flexDirection: "column", gap: theme.spacing(1.5),
    overflow: "hidden", boxSizing: "border-box",
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
      : "linear-gradient(145deg, #EBF3FF 0%, #F0F6FF 100%)",
  },

  // ── HERO ──────────────────────────────────────────────────────────────────
  hero: {
    flexShrink: 0, borderRadius: 16, padding: theme.spacing(1.5, 2.5),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0A1628 0%, #0D1F3C 100%)"
      : "linear-gradient(135deg, #EBF3FF 0%, #D6E9FF 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(0,140,255,0.18)" : "1px solid rgba(0,102,255,0.12)",
    boxShadow: "0 4px 16px rgba(0,102,255,0.07)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: theme.spacing(2), position: "relative", overflow: "hidden",
  },
  heroBgCircle: {
    position: "absolute", right: -20, top: -20, width: 140, height: 140,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroLeft: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), zIndex: 1 },
  heroIcon: {
    width: 48, height: 48, borderRadius: 14, flexShrink: 0, fontSize: "1.3rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 16px rgba(0,102,255,0.3)",
  },
  heroTitle: {
    fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.3px",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  heroTitleAccent: { color: "#0066FF" },
  heroSubtitle: {
    fontSize: "0.75rem", marginTop: 2,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.55)",
  },
  heroRight: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), zIndex: 1 },
  heroChip: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)",
    border: "1px solid rgba(0,102,255,0.15)", borderRadius: 10,
    fontWeight: 700, fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#0A1628",
  },
  addBtn: {
    borderRadius: 12, fontWeight: 700, fontSize: "0.82rem", textTransform: "none",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff", padding: theme.spacing(0.9, 2.5),
    boxShadow: "0 4px 14px rgba(0,102,255,0.28)",
    "&:hover": { background: "linear-gradient(135deg, #0052CC 0%, #007ACC 100%)" },
  },

  // ── METRICS ───────────────────────────────────────────────────────────────
  metricsRow: {
    flexShrink: 0, display: "flex", gap: theme.spacing(1.5),
  },
  metricCard: {
    flex: 1, borderRadius: 14, padding: theme.spacing(1.2, 2),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,102,255,0.08)",
    display: "flex", alignItems: "center", gap: theme.spacing(1.2),
  },
  metricIconWrap: {
    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  metricNum: {
    fontWeight: 800, fontSize: "1.4rem", lineHeight: 1,
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  metricLabel: {
    fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.4px", marginTop: 2,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.4)" : "rgba(10,22,40,0.45)",
  },

  // ── SEARCH ────────────────────────────────────────────────────────────────
  searchField: {
    flexShrink: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12, fontSize: "0.82rem",
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#ffffff",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0066FF" },
    },
  },

  // ── GRID ──────────────────────────────────────────────────────────────────
  grid: {
    flex: 1, minHeight: 0, overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: theme.spacing(1.5), alignContent: "start",
    ...theme.scrollbarStyles,
  },

  // ── CARD ──────────────────────────────────────────────────────────────────
  card: {
    borderRadius: 16,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    padding: theme.spacing(2), display: "flex", flexDirection: "column", gap: theme.spacing(1),
    transition: "all 0.18s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(0,102,255,0.10)",
      border: "1px solid rgba(0,102,255,0.2)",
    },
  },
  cardTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  cardIconWrap: {
    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 10px rgba(0,102,255,0.25)",
  },
  cardActions: { display: "flex", gap: 4 },
  cardActionBtn: {
    width: 28, height: 28, borderRadius: 8, padding: 0,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
    "&:hover": { background: "rgba(0,102,255,0.08)", borderColor: "rgba(0,102,255,0.3)" },
  },
  cardShortcode: {
    fontWeight: 700, fontSize: "0.92rem",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  cardPreview: {
    fontSize: "0.75rem", lineHeight: 1.4,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "rgba(10,22,40,0.5)",
    overflow: "hidden", textOverflow: "ellipsis",
    display: "-webkit-box", "-webkit-line-clamp": 2, "-webkit-box-orient": "vertical",
    minHeight: 32,
  },
  cardFooter: {
    display: "flex", alignItems: "center", gap: 6, marginTop: 2,
    paddingTop: theme.spacing(1),
    borderTop: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)",
  },
  cardBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    borderRadius: 6, padding: "2px 7px",
    fontSize: "0.65rem", fontWeight: 700,
  },
  emptyState: {
    gridColumn: "1 / -1",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: theme.spacing(6), gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const Quickemessages = () => {
  const classes = useStyles();
  const [loading, setLoading]                       = useState(false);
  const [pageNumber, setPageNumber]                 = useState(1);
  const [hasMore, setHasMore]                       = useState(false);
  const [selectedQuickemessage, setSelected]        = useState(null);
  const [deletingQuickemessage, setDeleting]        = useState(null);
  const [quickemessageModalOpen, setModalOpen]      = useState(false);
  const [confirmModalOpen, setConfirmModalOpen]     = useState(false);
  const [searchParam, setSearchParam]               = useState("");
  const [quickemessages, dispatch]                  = useReducer(reducer, []);
  const { user, socket }                            = useContext(AuthContext);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => fetchQuickemessages(), 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      if (data.action === "delete")
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
    };
    socket.on("company-" + companyId + "-quickmessage", onEvent);
    return () => socket.off("company-" + companyId + "-quickmessage", onEvent);
  }, [socket, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) { toastError(err); }
  };

  const handleDeleteQuickemessage = async (id) => {
    try {
      await api.delete("/quick-messages/" + id);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeleting(null); setSearchParam(""); setPageNumber(1);
    fetchQuickemessages(); dispatch({ type: "RESET" });
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight)
      setPageNumber(p => p + 1);
  };

  const total      = quickemessages.length;
  const withFile   = quickemessages.filter(m => !!m.mediaName).length;
  const editable   = quickemessages.filter(m => m.geral === true).length;

  return (
    <div className={classes.pageRoot}>
      <ConfirmationModal
        title={deletingQuickemessage && i18n.t("quickMessages.confirmationModal.deleteTitle") + " " + deletingQuickemessage.shortcode + "?"}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={() => { setPageNumber(1); fetchQuickemessages(); }}
        open={quickemessageModalOpen}
        onClose={() => { setSelected(null); setModalOpen(false); fetchQuickemessages(); }}
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />

      {/* HERO */}
      <div className={classes.hero}>
        <div className={classes.heroBgCircle} />
        <div className={classes.heroLeft}>
          <div className={classes.heroIcon}>⚡</div>
          <div>
            <Typography className={classes.heroTitle}>
              Respostas Rapidas
            </Typography>
            <Typography className={classes.heroSubtitle}>
              Centralize, padronize e acelere o atendimento com atalhos organizados
            </Typography>
          </div>
        </div>
        <div className={classes.heroRight}>
          <Chip
            icon={<FlashOnIcon style={{ fontSize: 14 }} />}
            label={total + " atalhos"}
            className={classes.heroChip}
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => { setSelected(null); setModalOpen(true); }}
            className={classes.addBtn}
            startIcon={<AddIcon style={{ fontSize: 16 }} />}
          >
            {i18n.t("quickMessages.buttons.add")}
          </Button>
        </div>
      </div>

      {/* MÉTRICAS */}
      <div className={classes.metricsRow}>
        <div className={classes.metricCard}>
          <div className={classes.metricIconWrap} style={{ background: "rgba(0,102,255,0.1)" }}>
            <FlashOnIcon style={{ color: "#0066FF", fontSize: 18 }} />
          </div>
          <div>
            <div className={classes.metricNum}>{total}</div>
            <div className={classes.metricLabel}>Total</div>
          </div>
        </div>
        <div className={classes.metricCard}>
          <div className={classes.metricIconWrap} style={{ background: "rgba(124,58,237,0.1)" }}>
            <AttachFileIcon style={{ color: "#7C3AED", fontSize: 18 }} />
          </div>
          <div>
            <div className={classes.metricNum} style={{ color: "#7C3AED" }}>{withFile}</div>
            <div className={classes.metricLabel}>Com arquivo</div>
          </div>
        </div>
        <div className={classes.metricCard}>
          <div className={classes.metricIconWrap} style={{ background: "rgba(0,168,107,0.1)" }}>
            <CheckCircleIcon style={{ color: "#00A86B", fontSize: 18 }} />
          </div>
          <div>
            <div className={classes.metricNum} style={{ color: "#00A86B" }}>{editable}</div>
            <div className={classes.metricLabel}>Editaveis</div>
          </div>
        </div>
      </div>

      {/* BUSCA */}
      <TextField
        placeholder={i18n.t("quickMessages.searchPlaceholder")}
        type="search"
        value={searchParam}
        onChange={e => setSearchParam(e.target.value.toLowerCase())}
        variant="outlined"
        size="small"
        className={classes.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "#0066FF", fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* GRID */}
      <div className={classes.grid} onScroll={handleScroll}>
        {quickemessages.length === 0 && !loading && (
          <div className={classes.emptyState}>
            <FlashOnIcon style={{ fontSize: "3rem", opacity: 0.3 }} />
            <Typography style={{ fontWeight: 600, fontSize: "0.9rem" }}>
              Nenhuma resposta rapida encontrada
            </Typography>
            <Typography style={{ fontSize: "0.78rem" }}>
              Crie sua primeira resposta clicando em "Adicionar"
            </Typography>
          </div>
        )}

        {quickemessages.map(msg => (
          <div key={msg.id} className={classes.card}>
            <div className={classes.cardTop}>
              <div className={classes.cardIconWrap}>
                <FlashOnIcon style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <div className={classes.cardActions}>
                <IconButton
                  size="small"
                  className={classes.cardActionBtn}
                  onClick={() => { setSelected(msg); setModalOpen(true); }}
                >
                  <EditIcon style={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  className={classes.cardActionBtn}
                  onClick={() => { setConfirmModalOpen(true); setDeleting(msg); }}
                >
                  <DeleteOutlineIcon style={{ fontSize: 14, color: "#EF4444" }} />
                </IconButton>
              </div>
            </div>

            <Typography className={classes.cardShortcode}>/{msg.shortcode}</Typography>

            <Typography className={classes.cardPreview}>
              {msg.message || "Sem mensagem de texto"}
            </Typography>

            <div className={classes.cardFooter}>
              {msg.mediaName ? (
                <span className={classes.cardBadge} style={{ background: "rgba(124,58,237,0.1)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.2)" }}>
                  <AttachFileIcon style={{ fontSize: 11 }} />
                  {msg.mediaName.length > 20 ? msg.mediaName.slice(0, 20) + "..." : msg.mediaName}
                </span>
              ) : (
                <span className={classes.cardBadge} style={{ background: "rgba(0,0,0,0.05)", color: "#94a3b8", border: "1px solid rgba(0,0,0,0.08)" }}>
                  Sem arquivo
                </span>
              )}
              {msg.geral === true && (
                <span className={classes.cardBadge} style={{ background: "rgba(0,168,107,0.1)", color: "#00A86B", border: "1px solid rgba(0,168,107,0.2)", marginLeft: "auto" }}>
                  <CheckCircleIcon style={{ fontSize: 11 }} />
                  Editavel
                </span>
              )}
            </div>
          </div>
        ))}

        {loading && [1,2,3,4,5,6].map(i => (
          <div key={i} className={classes.card} style={{ opacity: 0.4 }}>
            <div style={{ width: "40%", height: 14, borderRadius: 6, background: "#e2e8f0", marginBottom: 8 }} />
            <div style={{ width: "70%", height: 10, borderRadius: 6, background: "#e2e8f0", marginBottom: 4 }} />
            <div style={{ width: "55%", height: 10, borderRadius: 6, background: "#e2e8f0" }} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Quickemessages;
